<script setup lang="ts">
import type { LinkProps } from '/#/components/base/link'
import type { ComputedRef } from 'vue'

import get from 'lodash-es/get'
import isEqual from 'lodash-es/isEqual'
import isMatch from 'lodash-es/isMatch'
import isString from 'lodash-es/isString'

const { copyToClipboard } = useCopyToClipboard()
const { openRouteInModal } = useRouterModalStore()
const { openRouteInDrawer } = useRouterDrawerStore()
const { removePrefix } = useQueryPrefix()

const props = withDefaults(defineProps<LinkProps>(), {
  isModal: false,
  isDrawer: false,
  changeQueryParams: false,
  hideIcon: false,
})

const externalStarts = ['http', 'mailto', 'ftp']
const currentRoute = useRoute()
const routerLinkActive = 'router-link-active'

const isExternalLink: ComputedRef<boolean> = computed(() => {
  return isString(props.to) && externalStarts.some((start) => props.to.startsWith(start))
})

const target: ComputedRef<string> = computed(() => {
  if (props.to.startsWith('mailto')) return '_self'
  return '_blank'
})

const to = computed(() => {
  if (isExternalLink.value) return props.to

  if (props.changeQueryParams) {
    return {
      name: currentRoute.name,
      query: { ...currentRoute.query, ...props.to },
    }
  }
  if (isString(props.to)) return { name: props.to }
  return props.to
})

// eslint-disable-next-line sonarjs/cognitive-complexity
const activeClass = computed(() => {
  if (isExternalLink.value) return undefined

  // Check if query parameters should be changed
  // If changeQueryParams is true and to.name is not provided
  if (props.changeQueryParams && !props.to?.name) {
    // If activeLinkRef is given, match the query parameters with the active link reference
    if (props.activeLinkRef) {
      return isMatch(
        get(currentRoute.query, props.activeLinkRef) as Recordable,
        props.to[props.activeLinkRef],
      )
        ? routerLinkActive
        : ''
    }

    // Match the entire query parameters if activeLinkRef is not given
    const cleanedQuery = removePrefix(currentRoute.query)
    return isMatch(cleanedQuery, props.to) ? routerLinkActive : ''
  }

  // Check if the current route's hash should be matched
  // If the current route has a hash but the target route does not
  if (currentRoute?.hash && !props.to?.hash) {
    const pageName = isString(props.to) ? props.to : props.to?.name
    return currentRoute.name === pageName ? routerLinkActive : ''
  }

  // Check if the current route's name should be tracked with the target route name
  // If to.query is provided and trackWithName is true
  if (props.to?.query && props.trackWithName) {
    return currentRoute.name === props.to.name ? routerLinkActive : ''
  }

  // Check if query parameters are equal
  // If to.query is provided, compare the query parameters with the active link reference
  if (props.to?.query) {
    return isEqual(
      currentRoute.query[props.activeLinkRef as string],
      props.to.query[props.activeLinkRef as string],
    )
      ? routerLinkActive
      : ''
  }
})

const appearance: ComputedRef<Array<string | boolean>> = computed(() => [
  props.disabled && 'disabled',
  isExternalLink.value && 'external-link',
])

// Disable external link click event when disabled prop is true
function handleClick(event: Event) {
  if (props.disabled) {
    event.preventDefault()
  }
}

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <a
    v-if="isExternalLink"
    v-bind="$attrs"
    :href="props.to.toString()"
    :target="target"
    rel="noopener noreferrer"
    :class="appearance"
    @click="handleClick"
  >
    <slot>
      <span v-if="text" class="external-link-text"> {{ text }} </span>
    </slot>
    <!-- External link icon -->
    <Icon
      v-if="!hideIcon"
      class="external-link-icon"
     
      name="link-share"
      size="sm"
      color="inherit"
    />
    <Icon
      v-if="copyIcon"
      name="copy-icon"
      size="md"
      @click="copyToClipboard(to as string)"
    />
  </a>
  <RouterLink
    v-else-if="isModal"
    :class="appearance"
    v-bind="$attrs"
    :to="to"
    @click="openRouteInModal(to, modalProps)"
  >
    <span v-if="text"> {{ text }} </span>
    <slot />
  </RouterLink>
  <RouterLink
    v-else-if="isDrawer"
    :class="appearance"
    v-bind="$attrs"
    :to="to"
    @click="openRouteInDrawer(to, drawerProps)"
  >
    <span v-if="text"> {{ text }} </span>
    <slot />
  </RouterLink>
  <RouterLink
    v-else-if="to"
    v-slot="$slot"
    :class="appearance"
    v-bind="$attrs"
    :to="to"
    :replace="changeQueryParams"
    :active-class="activeClass"
    @click="handleClick"
  >
    <span v-if="text"> {{ text }} </span>
    <slot v-bind="$slot" />
  </RouterLink>
</template>
